import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { Box, Chip, Divider, Grid, IconButton, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { PersonSearch } from "@mui/icons-material";

import { getPodOrdersNotificationData, getPodsNotificationData, getTheftRegistryNotificationData } from '../../../../../../core/utils/notification_utils';
import useNotification from '../../../../../../core/hooks/use_notification';
import { getTheftRegistryAction, getTheftRegistryState } from '../../../../../../features/order/get_theft_registry';
import { getAllPodsAction, getAllPodsState } from '../../../../../../features/pod/get_all_pods';
import { getPodOrdersAction, getPodOrdersState } from '../../../../../../features/order/get_pod_orders';
import { getTheftRegistryStatus, getTheftRegistryStatusColor } from '../../../../../common/utils/theft_registry_utils';
import { ReturnPageButton } from '../../../../../common/button';
import DetailsDialog from '../common/details_dialog';
import { isEmptyArray } from '../../../../../../core/utils/misc_utils';
import { GET_ROUTE_CUSTOMER, MISC_DATE_ISO_DATE_ONLY_FORMAT, MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT, ROUTE_THEFT_REGISTRIES } from '../../../../../../core/constants';

const InfoField = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="textSecondary" display="block" sx={ {
            mb: 0.5,
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '0.7rem',
            letterSpacing: '0.5px'
        } }>
            { label }
        </Typography>
        <Typography variant="body1" sx={ { fontWeight: 500, color: 'text.primary' } }>
            { value !== undefined && value !== null ? value : 'Brak danych' }
        </Typography>
    </Box>
);

const TheftRegistryDetailsPage = () => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const { customerId, theftRegistryId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const getTheftRegistry = useSelector(getTheftRegistryState);
    const getAllPods = useSelector(getAllPodsState);
    const getPodOrders = useSelector(getPodOrdersState);

    useEffect(() => {
        dispatch(getTheftRegistryAction(theftRegistryId))
            .then(response => showErrorMessage(getTheftRegistryNotificationData(response)));
        dispatch(getAllPodsAction())
            .then(response => showErrorMessage(getPodsNotificationData(response)));
        dispatch(getPodOrdersAction({
            orderDate: moment(location?.state?.theftDate, MISC_DATE_ISO_FORMAT)?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
            customerId,
            podId: location?.state?.podId
        })).then(response => showErrorMessage(getPodOrdersNotificationData(response)));
    }, [customerId, dispatch, location?.state?.podId, location?.state?.theftDate, showErrorMessage, theftRegistryId]);

    const onReturnClick = () => {
        if (location?.state?.source === 'TheftRegistriesList') {
            navigate(ROUTE_THEFT_REGISTRIES, { state: { queryParams: location?.state?.queryParams } });
        } else {
            navigate(GET_ROUTE_CUSTOMER(customerId), { state: { tab: location?.state?.tab } });
        }
    };

    const theftData = getTheftRegistry?.data;

    const currentPod = getAllPods?.data?.find(pod => pod?.id === theftData?.podId);
    const podLabel = currentPod
        ? `${ currentPod?.indoorPartner?.name } (${ currentPod?.description }) - ${ currentPod?.device?.serviceId || 'brak urządzenia' }`
        : 'Wyszukiwanie punktu...';

    const currentOrder = getPodOrders?.data?.find(order => order?.id === theftData?.incompleteOrderId);
    const orderLabel = currentOrder
        ? `Data: ${ moment(currentOrder?.createdDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }, wartość: ${ currentOrder?.totalPrice } ${ currentOrder?.currency }, produkty: ${ currentOrder?.numberOfProducts } szt.`
        : 'Brak bezpośrednio powiązanego zamówienia';

    const isLoading = getTheftRegistry?.loading || getAllPods?.loading || getPodOrders?.loading;

    return (
        <Grid container rowGap={ 3 }>
            { isLoading && (
                <Grid item width="100%">
                    <LinearProgress/>
                </Grid>
            ) }

            { !isLoading && theftData && (
                <Grid container rowGap={ 3 }>

                    <Grid item xs={ 12 } sx={ { mb: 1 } }>
                        <Stack direction="row" alignItems="center" spacing={ 2 }>
                            <ReturnPageButton onClick={ onReturnClick }/>
                            <Typography variant="h4" component="h1" sx={ { fontWeight: 600, color: 'text.primary' } }>
                                Transakcja T{ theftRegistryId }
                            </Typography>
                            <Chip
                                label={ getTheftRegistryStatus(theftData?.status) }
                                color={ getTheftRegistryStatusColor(theftData?.status) }
                                sx={ { fontWeight: 600 } }
                            />
                        </Stack>
                    </Grid>


                    <Paper variant="outlined" sx={ { p: 3, width: '100%', backgroundColor: 'background.paper' } }>
                        <Grid container spacing={ 4 }>
                            <Grid item xs={ 12 } sm={ 3 }>
                                <InfoField
                                    label="Data kradzieży"
                                    value={ theftData?.theftDate ? moment(theftData.theftDate).format(MISC_DATE_POLISH_FORMAT) : '' }
                                />
                            </Grid>
                            <Grid item xs={ 12 } sm={ 3 }>
                                <Typography variant="caption" color="textSecondary" display="block" sx={ {
                                    mb: 1,
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                    letterSpacing: '0.5px'
                                } }>
                                    Częściowa kradzież
                                </Typography>
                                <Chip
                                    label={ theftData?.partialTheft ? "TAK" : "NIE" }
                                    color="secondary"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={ 12 } sm={ 3 }>
                                <Typography variant="caption" color="textSecondary" display="block" sx={ {
                                    mb: 1,
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                    letterSpacing: '0.5px'
                                } }>
                                    Wysłano SMS
                                </Typography>
                                <Chip
                                    label={ theftData?.smsSent ? "Wysłano" : "Nie wysłano" }
                                    color={ theftData?.smsSent ? "success" : "default" }
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={ 12 } sm={ 3 }>
                                <InfoField
                                    label="Liczba wysłanych SMS"
                                    value={ theftData?.smsSentCount !== undefined ? theftData.smsSentCount : 0 }
                                />
                            </Grid>

                            <Grid item xs={ 12 }>
                                <Divider/>
                            </Grid>

                            <Grid item xs={ 12 } md={ 6 }>
                                <InfoField label="Punkt sprzedaży (POD)" value={ podLabel }/>
                            </Grid>

                            <Grid item xs={ 12 } md={ 6 }>
                                <Stack direction="row" alignItems="center" spacing={ 1 }>
                                    { location?.state?.source === 'TheftRegistriesList' && (
                                        <Tooltip title="Przekierowanie do danych klienta">
                                            <IconButton
                                                onClick={ () => navigate(GET_ROUTE_CUSTOMER(customerId)) }
                                                size="small"
                                                sx={ { mt: 2 } }
                                            >
                                                <PersonSearch/>
                                            </IconButton>
                                        </Tooltip>
                                    ) }
                                    <InfoField label="ID klienta" value={ customerId }/>
                                </Stack>
                            </Grid>

                            { !isEmptyArray(getPodOrders?.data) && (
                                <>
                                    <Grid item xs={ 12 }>
                                        <Divider/>
                                    </Grid>
                                    <Grid item xs={ 12 }>
                                        <InfoField label="Zamówienie powiązane z kradzieżą" value={ orderLabel }/>
                                    </Grid>
                                </>
                            ) }
                        </Grid>
                    </Paper>


                    <Grid container item>
                        <Typography variant="h6" gutterBottom sx={ { fontWeight: 600, mb: 2 } }>
                            Pozycje kartoteki ({ theftData?.positions?.length || 0 })
                        </Typography>
                        <TableContainer component={ Paper } variant="outlined">
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow sx={ { backgroundColor: 'action.hover' } }>
                                        <TableCell style={ { width: '5%', fontWeight: 600 } }>Lp.</TableCell>
                                        <TableCell style={ { fontWeight: 600 } }>Nazwa produktu</TableCell>
                                        <TableCell align="center" style={ { width: '12%', fontWeight: 600 } }>Liczba
                                            szt.</TableCell>
                                        <TableCell align="center"
                                                   style={ { width: '12%', fontWeight: 600 } }>Szczegóły</TableCell>
                                        <TableCell align="right" style={ { width: '18%', fontWeight: 600 } }>Cena
                                            sprzedaży [brutto]</TableCell>
                                        <TableCell align="right" style={ { width: '18%', fontWeight: 600 } }>Cena
                                            ostateczna [brutto]</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    { theftData?.positions?.map((product, index) => (
                                        <TableRow key={ index } hover>
                                            <TableCell>{ index + 1 }</TableCell>
                                            <TableCell sx={ { fontWeight: 500 } }>{ product?.productName }</TableCell>
                                            <TableCell align="center">{ product?.quantity }</TableCell>
                                            <TableCell align="center">
                                                <DetailsDialog product={ product }/>
                                            </TableCell>
                                            <TableCell align="right">
                                                { product?.sellingPrice ? `${ product.sellingPrice.toFixed(2) } PLN` : '-' }
                                            </TableCell>
                                            <TableCell align="right" sx={ { fontWeight: 600, color: 'text.primary' } }>
                                                { product?.shopAdminSellingPrice ? `${ product.shopAdminSellingPrice.toFixed(2) } PLN` : '-' }
                                            </TableCell>
                                        </TableRow>
                                    )) }
                                    { isEmptyArray(theftData?.positions) && (
                                        <TableRow>
                                            <TableCell colSpan={ 6 } align="center"
                                                       sx={ { py: 3, color: 'text.secondary' } }>
                                                Brak pozycji w tej kartotece.
                                            </TableCell>
                                        </TableRow>
                                    ) }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            ) }
        </Grid>
    );
};

export default TheftRegistryDetailsPage;